import React from "react";
import {
  Routes,
  Route,
  Navigate,
  useParams,
  useLocation,
} from "react-router-dom";
import Dashboard from "../pages/Dashboard";
import LogIn from "../pages/Onboarding/Login";
import Onboarding from "pages/Onboarding";
import CreateOrganization from "features/Organisation/features/CreateOrganisation";
import Building from "../pages/Building";
import BuildingDetail from "features/Building/BuildingPassport";
import BuildingSourceFiles from "features/Building/BuildingSourceFiles";
import { useAuth0 } from "@auth0/auth0-react";
import Verify from "pages/Onboarding/Verify";
import BuildingInventory from "features/Building/BuildingInventory";
import BimViewerPage from "pages/Building/BimViewerPage";
import Exports from "pages/Building/Exports";
import BuildingProductEditor from "features/BuildingProductEditor";
import Revit from "pages/Revit";
import RevitConnectedView from "features/Revit/RevitConnectedView";
import TermsAndConditions from "pages/TermsAndConditions";
import Variants from "features/Building/Variants";
import { userStore } from "store/UserStore";
import { observer } from "mobx-react-lite";
import ComponentEditor from "features/ComponentEditor";
import AuditMaterialSales from "pages/Retool/SalesApproval";
import PortfolioDashboard from "pages/Retool/PortfolioDashboard";
import UrbanMiningHub from "pages/Retool/UrbanMiningHub";
import {
  CONCULAR_ONE_URL,
  IS_CONCULAR_ONE,
  PORTFOLIO_URL,
  URBAN_MINING_HUB_URL,
} from "utils";
import MappingEditorPage from "pages/Building/MappingEditorPage";
import PageAI from "pages/Building/AI";
import EnvironmentalImpactDashboard from "pages/Retool/EnvironmentalImpactDashboard";
import PdfTemplate from "pages/Building/PdfTemplate";
import ConcularOne from "pages/one";
import EulePage from "pages/Building/Eule";
import CO2HotspotAnalyzer from "pages/Retool/CO2HotspotAnalyzer";
import MaterialValueDashboard from "pages/Retool/MaterialValueDashboard";
import CircularityDashboard from "pages/Retool/CircularityDashboard";
import EnergyDashboard from "pages/Retool/EnergyDashboard";
import DataQualityDashboard from "pages/Retool/DataQualityDashboard";
import CircularAssetPortfolio from "pages/Retool/CircularAssetPortfolio";
import RulesEditor from "features/RulesSet/pages/RulesSetEditor";
import EmissionsFromOperation from "pages/Retool/EmissionsFromOperation";
import { ROUTES_ENUM } from "./consts";
import PortfolioScreener from "pages/Retool/PortfolioScreener";
import BuildingNoLicense from "pages/Building/BuildingNoLicense";
import DGNB18Dashboard from "pages/Retool/DNGB18Dashboard";
import DGNB23Dashboard from "pages/Retool/DGNB23Dashboard";
import QNGDashboard from "pages/Retool/QNGDashboard";
import OperationalEmissions from "features/OperationalEmissions";
import ManufacturerDashboard from "pages/Retool/ManufacturerDashboard";
import ManufacturerProductList from "pages/Manufacturer/ProductList";
import OrganisationEPDList from "pages/Manufacturer/EPDList";
import EPDEditor from "features/EPDEditor";
import Inventory from "features/Inventory";
import LCCPage from "features/LCC";
import LevelsDashboard from "features/LevelsDashboard";

const AppRoutes = () => {
  const { isAuthenticated } = useAuth0();
  const { onboarded, verified } = userStore;

  const location = useLocation();

  function mainPageRedirection() {
    if (!onboarded) {
      return `/${ROUTES_ENUM.ONBOARDING}`;
    }
    // user verification comes after onboarding
    if (!verified) {
      return `/${ROUTES_ENUM.VERIFY}`;
    }
    if (IS_CONCULAR_ONE) {
      return `/${ROUTES_ENUM.ONE}`;
    } else if (
      location.pathname.includes(`/${ROUTES_ENUM.ONE}`) &&
      !window.location.href.includes("localhost")
    ) {
      window.location.href = CONCULAR_ONE_URL;
    }
    return `/${ROUTES_ENUM.DASHBOARD}`;
  }

  function onboardingRoutes() {
    return (
      <>
        <Route path={`/${ROUTES_ENUM.ONBOARDING}`} element={<Onboarding />} />
        <Route path={`/${ROUTES_ENUM.VERIFY}`} element={<Verify />} />
        <Route
          path={`/${ROUTES_ENUM.ONBOARDING}/create-organisation`}
          element={<CreateOrganization />}
        />
      </>
    );
  }

  function buildingRoutes() {
    return (
      <Route path={`/${ROUTES_ENUM.BUILDINGS}/:id/`} element={<Building />}>
        <Route
          path=""
          element={<Navigate replace to={ROUTES_ENUM.PASSPORT} />}
        />
        <Route
          path={ROUTES_ENUM.AUDIT_MATERIAL_SALES}
          element={<AuditMaterialSales />}
        />
        <Route path={ROUTES_ENUM.PASSPORT} element={<BuildingDetail />} />
        <Route path={ROUTES_ENUM.EXPORTS} element={<Exports />} />
        <Route
          path={ROUTES_ENUM.ANALYTICS}
          element={<EnvironmentalImpactDashboard />}
        />
        <Route
          path={ROUTES_ENUM.MATERIAL_VALUE}
          element={<MaterialValueDashboard />}
        />
        <Route
          path={ROUTES_ENUM.CIRCULARITY}
          element={<CircularityDashboard />}
        />
        <Route path={ROUTES_ENUM.ENERGY} element={<EnergyDashboard />} />
        <Route
          path={ROUTES_ENUM.DATA_QUALITY}
          element={<DataQualityDashboard />}
        />
        <Route path={ROUTES_ENUM.PORTFOLIO} element={<PortfolioDashboard />} />
        <Route
          path={ROUTES_ENUM.DGNB18_DASHBOARD}
          element={<DGNB18Dashboard />}
        />
        <Route
          path={ROUTES_ENUM.DGNB23_DASHBOARD}
          element={<DGNB23Dashboard />}
        />
        <Route path={ROUTES_ENUM.QNG_DASHBOARD} element={<QNGDashboard />} />
        <Route
          path={ROUTES_ENUM.LEVELS_DASHBOARD}
          element={<LevelsDashboard />}
        />
        <Route
          path={ROUTES_ENUM.SOURCE_FILES}
          element={<BuildingSourceFiles />}
        />
        <Route
          path={ROUTES_ENUM.OPERATIONAL_ENERGY}
          element={<OperationalEmissions />}
        />
        <Route path={ROUTES_ENUM.VARIANTS} element={<Variants />} />
        <Route path={ROUTES_ENUM.AI} element={<PageAI />} />
        <Route path={ROUTES_ENUM.EEULE} element={<EulePage />} />
        <Route
          path={ROUTES_ENUM.HOTSPOT_ANALYZER}
          element={<CO2HotspotAnalyzer />}
        />
        <Route path={ROUTES_ENUM.PDF_TEMPLATE} element={<PdfTemplate />} />
        <Route
          path={ROUTES_ENUM.EMISSIONS}
          element={<EmissionsFromOperation />}
        />
        <Route
          path={`${ROUTES_ENUM.MAPPING_EDITOR}/:ifc_id`}
          element={<MappingEditorPage />}
        />
        <Route
          path={`${ROUTES_ENUM.VIEWER}/:ifc_id`}
          element={<BimViewerPage />}
        />
        <Route path={ROUTES_ENUM.INVENTORY} element={<BuildingInventory />} />
        <Route path={ROUTES_ENUM.NEW_INVENTORY} element={<Inventory />} />
        <Route
          path={`${ROUTES_ENUM.MAPPING_EDITOR}/:ifc_id/${ROUTES_ENUM.PRODUCT_EDITOR}`}
          element={<BuildingProductEditor />}
        />
        <Route
          path={`${ROUTES_ENUM.MAPPING_EDITOR}/:ifc_id/${ROUTES_ENUM.COMPONENT_EDITOR}`}
          element={<ComponentEditor />}
        />
        <Route path={ROUTES_ENUM.LCC} element={<LCCPage />} />
        <Route
          path={`${ROUTES_ENUM.MAPPING_EDITOR}/:ifc_id/${ROUTES_ENUM.EPD_EDITOR}`}
          element={<EPDEditor />}
        />
      </Route>
    );
  }

  function revitRoutes() {
    return (
      <Route path={`/${ROUTES_ENUM.REVIT_SETUP}/:lang`}>
        <Route path="" element={<RevitView />} />
        <Route path="connected" element={<RevitConnected />} />
      </Route>
    );
  }

  function RevitView() {
    const { lang } = useParams();
    const language = lang || "en";

    return <Revit lang={language} />;
  }

  function RevitConnected() {
    const { lang } = useParams();
    const language = lang || "en";

    return <RevitConnectedView lang={language} />;
  }

  function ConcularOneRoutes() {
    return (
      <Route path={`/${ROUTES_ENUM.ONE}`}>
        <Route path="" element={<ConcularOne />} />
      </Route>
    );
  }

  return (
    <Routes>
      {!isAuthenticated ? (
        <Route path="*" element={<LogIn />} />
      ) : (
        <>
          <Route
            path="/"
            element={<Navigate to={mainPageRedirection()} replace />}
          />
          <Route path={`/${ROUTES_ENUM.DASHBOARD}`} element={<Dashboard />} />
          {onboardingRoutes()}
          {ConcularOneRoutes()}
          {IS_CONCULAR_ONE ? (
            <Route
              path="*"
              element={<Navigate to={`/${ROUTES_ENUM.ONE}`} replace />}
            />
          ) : (
            <>
              <Route
                path={`/${ROUTES_ENUM.BUILDING_NO_LICENSE}/:id`}
                element={<BuildingNoLicense />}
              />
              {buildingRoutes()}
              <Route
                path={`:organisation_id/${PORTFOLIO_URL}`}
                element={<PortfolioDashboard />}
              />
              <Route
                path={`:organisation_id/${ROUTES_ENUM.PORTFOLIO_SCREENER}`}
                element={<PortfolioScreener />}
              />
              <Route
                path={`:organisation_id/${URBAN_MINING_HUB_URL}`}
                element={<UrbanMiningHub />}
              />
              <Route
                path={`:organisation_id/${ROUTES_ENUM.MANUFACTURER_DASHBOARD}`}
                element={<ManufacturerDashboard />}
              />
              <Route
                path={`:organisation_id/${ROUTES_ENUM.ORGANIZATION_PRODUCT_LIST}`}
                element={<ManufacturerProductList />}
              />
              <Route
                path={`:organisation_id/${ROUTES_ENUM.ORGANIZATION_EPD_LIST}`}
                element={<OrganisationEPDList />}
              />
              <Route
                path={`:organisation_id/${ROUTES_ENUM.ORGANIZATION_EPD_LIST}/${ROUTES_ENUM.EPD_EDITOR}`}
                element={<EPDEditor />}
              />
              <Route
                path={`:organisation_id/${ROUTES_ENUM.ORGANIZATION_EPD_LIST}/${ROUTES_ENUM.EPD_EDITOR}`}
                element={<EPDEditor />}
              />
              <Route
                path={`:organisation_id/${ROUTES_ENUM.ORGANIZATION_PRODUCT_LIST}/${ROUTES_ENUM.PRODUCT_EDITOR}`}
                element={<BuildingProductEditor />}
              />
              <Route
                path={`:organisation_id/${ROUTES_ENUM.ORGANIZATION_PRODUCT_LIST}/${ROUTES_ENUM.COMPONENT_EDITOR}`}
                element={<ComponentEditor />}
              />
              <Route
                path={`:organisation_id/${ROUTES_ENUM.ORGANIZATION_PRODUCT_LIST}/${ROUTES_ENUM.EPD_EDITOR}`}
                element={<EPDEditor />}
              />
              <Route
                path={`:organisation_id/${ROUTES_ENUM.PORTFOLIO_ASSETS}`}
                element={<CircularAssetPortfolio />}
              />
              {revitRoutes()}
              <Route
                path={`:organisation_id/${ROUTES_ENUM.RULES_EDITOR}`}
                element={<RulesEditor />}
              />
            </>
          )}
          <Route
            path={`/${ROUTES_ENUM.TERMS_CONDITIONS}`}
            element={<TermsAndConditions />}
          />
        </>
      )}
    </Routes>
  );
};

export default observer(AppRoutes);
